import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Percent, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin-only card that controls the % of each Experience / Reservation
 * transaction that gets retained as the platform fee. Persists to
 * `db.platform_settings` via /api/admin/platform-settings.
 */
export default function PlatformFeeSettingsCard({ token }) {
  const [pct, setPct] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/admin/platform-settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setPct(String(res.data?.platform_fee_pct ?? 10)))
      .catch(() => setPct('10'))
      .finally(() => setLoading(false));
  }, [token]);

  const save = async () => {
    const num = parseFloat(pct);
    if (isNaN(num) || num < 0 || num > 50) {
      toast.error('Enter a number between 0 and 50');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/platform-settings`,
        { platform_fee_pct: num },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Platform fee set to ${num}%`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="settings-platform-fee">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Percent className="h-5 w-5 text-emerald-500" />
          Platform Fee
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Percentage of every Experience / Reservation payment that Eventism retains. Default is <strong>10%</strong>. Applies to all new Stripe Checkout sessions (existing sessions keep the rate they were created with).
        </p>
        <div className="flex items-end gap-2 max-w-sm">
          <div className="flex-1 space-y-1">
            <Label className="text-xs">Fee %</Label>
            {loading ? (
              <div className="h-10 flex items-center"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
            ) : (
              <Input
                type="number"
                min={0}
                max={50}
                step="0.1"
                value={pct}
                onChange={(e) => setPct(e.target.value)}
                data-testid="platform-fee-input"
              />
            )}
          </div>
          <Button onClick={save} disabled={saving || loading} data-testid="platform-fee-save">
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
